import React, { useState, useEffect } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import InfeedSettingsV2 from './InfeedSettingsV2';
import OutfeedSettings from './OutfeedSettings';
import OutfeedSettingsV2 from './OutfeedSettingsV2';

interface ArmTimingData {
  stationNumber: number;
  armOpenDelay: number | '';
  armOpenDuration: number | '';
}

interface SystemDefaults {
  id: string;
  conveyorStartDelay: number;
  conveyorStopDelay: number;
  bufferHopperOpenDelay: number;
  bufferHopperCloseDelay: number;
  weighingDelay: number;
  zeroingDelay: number;
  zeroThresholdWeight: number;
  hopperCapacity: number;
  defaultInfeedTargetWeight: number;
  // Outfeed settings
  outfeedHopperCapacity: number;
  outfeedCycleWaitTime: number;
  outfeedWeighDelay: number;
  outfeedZeroDelay: number;
  outfeedZeroThresholdWeight: number;
  outfeedBreakoutAttempts: number;
  outfeedPocketBufferHopperCloseDelay: number;
  defaultPocketCloseDelays: string;
  // System settings
  defaultNumberOfStations: number;
  defaultNumberOfPockets: number;
  lockNumberOfStations: boolean;
  lockNumberOfPockets: boolean;
  // Arm timing settings
  armTimingSettings?: ArmTimingData[];
}

interface DefaultsFormData {
  conveyorStartDelay: number | '';
  conveyorStopDelay: number | '';
  bufferHopperOpenDelay: number | '';
  bufferHopperCloseDelay: number | '';
  weighingDelay: number | '';
  zeroingDelay: number | '';
  zeroThresholdWeight: number | '';
  hopperCapacity: number | '';
  defaultInfeedTargetWeight: number | '';
  // Outfeed settings
  outfeedHopperCapacity: number | '';
  outfeedCycleWaitTime: number | '';
  outfeedWeighDelay: number | '';
  outfeedZeroDelay: number | '';
  outfeedZeroThresholdWeight: number | '';
  outfeedBreakoutAttempts: number | '';
  outfeedPocketBufferHopperCloseDelay: number | '';
  defaultPocketCloseDelays: (number | '')[];
  // System settings
  defaultNumberOfStations: number | '';
  defaultNumberOfPockets: number | '';
  lockNumberOfStations: boolean;
  lockNumberOfPockets: boolean;
  // Arm timing settings
  armTimingSettings: ArmTimingData[];
}


type DefaultsSubTab = 'system' | 'infeed' | 'outfeed';

const DefaultsSettings: React.FC = () => {
  const { apiUrl } = useDatabaseContext();
  const [activeSubTab, setActiveSubTab] = useState<DefaultsSubTab>('system');
  const [showDeleteEmployeesConfirm, setShowDeleteEmployeesConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState<DefaultsFormData>({
    conveyorStartDelay: 1000,
    conveyorStopDelay: 2000,
    bufferHopperOpenDelay: 500,
    bufferHopperCloseDelay: 1500,
    weighingDelay: 3000,
    zeroingDelay: 1000,
    zeroThresholdWeight: 0.1,
    hopperCapacity: 50.0,
    defaultInfeedTargetWeight: 10.0,
    // Outfeed defaults
    outfeedHopperCapacity: 50.0,
    outfeedCycleWaitTime: 2000,
    outfeedWeighDelay: 3000,
    outfeedZeroDelay: 1000,
    outfeedZeroThresholdWeight: 0.1,
    outfeedBreakoutAttempts: 3,
    outfeedPocketBufferHopperCloseDelay: 1500,
    defaultPocketCloseDelays: [1500, 1500, 1500, 1500],
    // System defaults
    defaultNumberOfStations: 10,
    defaultNumberOfPockets: 3,
    lockNumberOfStations: true,
    lockNumberOfPockets: true,
    // Arm timing defaults
    armTimingSettings: []
  });
  const [errors, setErrors] = useState<Record<string, string>>({
    conveyorStartDelay: '',
    conveyorStopDelay: '',
    bufferHopperOpenDelay: '',
    bufferHopperCloseDelay: '',
    weighingDelay: '',
    zeroingDelay: '',
    zeroThresholdWeight: '',
    hopperCapacity: '',
    defaultInfeedTargetWeight: '',
    // Outfeed errors
    outfeedHopperCapacity: '',
    outfeedCycleWaitTime: '',
    outfeedWeighDelay: '',
    outfeedZeroDelay: '',
    outfeedZeroThresholdWeight: '',
    outfeedBreakoutAttempts: '',
    outfeedPocketBufferHopperCloseDelay: '',
    // System errors
    defaultNumberOfStations: '',
    defaultNumberOfPockets: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Fetch current system defaults
  useEffect(() => {
    const fetchDefaults = async () => {
      try {
        const response = await fetch(`${apiUrl}/system-defaults`);
        if (response.ok) {
          const data = await response.json();
          const defaults = data.data;
          const armTimingSettings = defaults.armTimingSettings ? 
            (typeof defaults.armTimingSettings === 'string' ? 
              JSON.parse(defaults.armTimingSettings) : 
              defaults.armTimingSettings) : [];
              
          setFormData({
            conveyorStartDelay: defaults.conveyorStartDelay,
            conveyorStopDelay: defaults.conveyorStopDelay,
            bufferHopperOpenDelay: defaults.bufferHopperOpenDelay,
            bufferHopperCloseDelay: defaults.bufferHopperCloseDelay,
            weighingDelay: defaults.weighingDelay,
            zeroingDelay: defaults.zeroingDelay,
            zeroThresholdWeight: defaults.zeroThresholdWeight,
            hopperCapacity: defaults.hopperCapacity,
            defaultInfeedTargetWeight: defaults.defaultInfeedTargetWeight,
            // Outfeed defaults
            outfeedHopperCapacity: defaults.outfeedHopperCapacity || 50.0,
            outfeedCycleWaitTime: defaults.outfeedCycleWaitTime || 2000,
            outfeedWeighDelay: defaults.outfeedWeighDelay || 3000,
            outfeedZeroDelay: defaults.outfeedZeroDelay || 1000,
            outfeedZeroThresholdWeight: defaults.outfeedZeroThresholdWeight || 0.1,
            outfeedBreakoutAttempts: defaults.outfeedBreakoutAttempts || 3,
            outfeedPocketBufferHopperCloseDelay: defaults.outfeedPocketBufferHopperCloseDelay || 1500,
            defaultPocketCloseDelays: JSON.parse(defaults.defaultPocketCloseDelays || '[1500, 1500, 1500, 1500]'),
            // System defaults
            defaultNumberOfStations: defaults.defaultNumberOfStations || 10,
            defaultNumberOfPockets: defaults.defaultNumberOfPockets || 3,
            lockNumberOfStations: defaults.lockNumberOfStations ?? true,
            lockNumberOfPockets: defaults.lockNumberOfPockets ?? true,
            // Arm timing settings
            armTimingSettings: armTimingSettings
          });
        }
      } catch (error) {
        console.error('Error fetching system defaults:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDefaults();
  }, [apiUrl]);

  const handleInfeedChange = (field: keyof DefaultsFormData, value: number | '' | ArmTimingData[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleOutfeedChange = (field: keyof DefaultsFormData, value: number | '') => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSystemChange = (field: keyof DefaultsFormData, value: number | '') => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePocketDelayChange = (index: number, value: number | '') => {
    setFormData(prev => {
      const newDelays = [...prev.defaultPocketCloseDelays];
      newDelays[index] = value;
      return { ...prev, defaultPocketCloseDelays: newDelays };
    });
  };

  const validate = (): boolean => {
    let isValid = true;
    const newErrors = {
      conveyorStartDelay: '',
      conveyorStopDelay: '',
      bufferHopperOpenDelay: '',
      bufferHopperCloseDelay: '',
      weighingDelay: '',
      zeroingDelay: '',
      zeroThresholdWeight: '',
      hopperCapacity: '',
      defaultInfeedTargetWeight: '',
      // Outfeed errors
      outfeedHopperCapacity: '',
      outfeedCycleWaitTime: '',
      outfeedWeighDelay: '',
      outfeedZeroDelay: '',
      outfeedZeroThresholdWeight: '',
      outfeedBreakoutAttempts: '',
      outfeedPocketBufferHopperCloseDelay: '',
      // System errors
      defaultNumberOfStations: '',
      defaultNumberOfPockets: '',
    };

    // Validate all timing fields (must be positive integers)
    const timingFields = ['conveyorStartDelay', 'conveyorStopDelay', 'bufferHopperOpenDelay', 'bufferHopperCloseDelay', 'weighingDelay', 'zeroingDelay'] as const;
    timingFields.forEach(field => {
      const value = formData[field];
      if (value === '' || isNaN(Number(value)) || Number(value) < 0) {
        newErrors[field] = 'Must be a positive number.';
        isValid = false;
      }
    });

    // Validate weight fields (must be positive floats)
    if (formData.zeroThresholdWeight === '' || isNaN(Number(formData.zeroThresholdWeight)) || Number(formData.zeroThresholdWeight) < 0) {
      newErrors.zeroThresholdWeight = 'Must be a positive number.';
      isValid = false;
    }

    if (formData.hopperCapacity === '' || isNaN(Number(formData.hopperCapacity)) || Number(formData.hopperCapacity) <= 0) {
      newErrors.hopperCapacity = 'Must be a positive number.';
      isValid = false;
    }

    if (formData.defaultInfeedTargetWeight === '' || isNaN(Number(formData.defaultInfeedTargetWeight)) || Number(formData.defaultInfeedTargetWeight) <= 0) {
      newErrors.defaultInfeedTargetWeight = 'Must be a positive number.';
      isValid = false;
    }

    // Validate outfeed timing fields
    const outfeedTimingFields = ['outfeedCycleWaitTime', 'outfeedWeighDelay', 'outfeedZeroDelay', 'outfeedBreakoutAttempts', 'outfeedPocketBufferHopperCloseDelay'] as const;
    outfeedTimingFields.forEach(field => {
      const value = formData[field];
      if (value === '' || isNaN(Number(value)) || Number(value) < 0) {
        newErrors[field] = 'Must be a positive number.';
        isValid = false;
      }
    });

    // Validate outfeed weight fields
    if (formData.outfeedZeroThresholdWeight === '' || isNaN(Number(formData.outfeedZeroThresholdWeight)) || Number(formData.outfeedZeroThresholdWeight) < 0) {
      newErrors.outfeedZeroThresholdWeight = 'Must be a positive number.';
      isValid = false;
    }

    if (formData.outfeedHopperCapacity === '' || isNaN(Number(formData.outfeedHopperCapacity)) || Number(formData.outfeedHopperCapacity) <= 0) {
      newErrors.outfeedHopperCapacity = 'Must be a positive number.';
      isValid = false;
    }

    // Validate system fields
    if (formData.defaultNumberOfStations === '' || isNaN(Number(formData.defaultNumberOfStations)) || Number(formData.defaultNumberOfStations) < 1) {
      newErrors.defaultNumberOfStations = 'Must be at least 1.';
      isValid = false;
    }

    if (formData.defaultNumberOfPockets === '' || isNaN(Number(formData.defaultNumberOfPockets)) || Number(formData.defaultNumberOfPockets) < 1) {
      newErrors.defaultNumberOfPockets = 'Must be at least 1.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    try {
      const defaultsData = {
        id: 'system',
        conveyorStartDelay: Number(formData.conveyorStartDelay),
        conveyorStopDelay: Number(formData.conveyorStopDelay),
        bufferHopperOpenDelay: Number(formData.bufferHopperOpenDelay),
        bufferHopperCloseDelay: Number(formData.bufferHopperCloseDelay),
        weighingDelay: Number(formData.weighingDelay),
        zeroingDelay: Number(formData.zeroingDelay),
        zeroThresholdWeight: Number(formData.zeroThresholdWeight),
        hopperCapacity: Number(formData.hopperCapacity),
        defaultInfeedTargetWeight: Number(formData.defaultInfeedTargetWeight),
        // Outfeed data
        outfeedHopperCapacity: Number(formData.outfeedHopperCapacity),
        outfeedCycleWaitTime: Number(formData.outfeedCycleWaitTime),
        outfeedWeighDelay: Number(formData.outfeedWeighDelay),
        outfeedZeroDelay: Number(formData.outfeedZeroDelay),
        outfeedZeroThresholdWeight: Number(formData.outfeedZeroThresholdWeight),
        outfeedBreakoutAttempts: Number(formData.outfeedBreakoutAttempts),
        outfeedPocketBufferHopperCloseDelay: Number(formData.outfeedPocketBufferHopperCloseDelay),
        defaultPocketCloseDelays: JSON.stringify(formData.defaultPocketCloseDelays.map(delay => Number(delay) || 1500)),
        // System data
        defaultNumberOfStations: Number(formData.defaultNumberOfStations),
        defaultNumberOfPockets: Number(formData.defaultNumberOfPockets),
        lockNumberOfStations: formData.lockNumberOfStations,
        lockNumberOfPockets: formData.lockNumberOfPockets,
        // Arm timing data
        armTimingSettings: JSON.stringify(formData.armTimingSettings)
      };

      const response = await fetch(`${apiUrl}/system-defaults`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(defaultsData)
      });

      if (response.ok) {
        console.log('System defaults saved successfully');
        setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        console.error('Error saving system defaults');
        setSaveMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
        setTimeout(() => setSaveMessage(null), 5000);
      }
    } catch (error) {
      console.error('Error saving system defaults:', error);
      setSaveMessage({ type: 'error', text: 'An error occurred while saving. Please try again.' });
      setTimeout(() => setSaveMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      conveyorStartDelay: 1000,
      conveyorStopDelay: 2000,
      bufferHopperOpenDelay: 500,
      bufferHopperCloseDelay: 1500,
      weighingDelay: 3000,
      zeroingDelay: 1000,
      zeroThresholdWeight: 0.1,
      hopperCapacity: 50.0,
      defaultInfeedTargetWeight: 10.0,
      // Outfeed defaults
      outfeedHopperCapacity: 50.0,
      outfeedCycleWaitTime: 2000,
      outfeedWeighDelay: 3000,
      outfeedZeroDelay: 1000,
      outfeedZeroThresholdWeight: 0.1,
      outfeedBreakoutAttempts: 3,
      outfeedPocketBufferHopperCloseDelay: 1500,
      defaultPocketCloseDelays: [1500, 1500, 1500, 1500],
      // System defaults
      defaultNumberOfStations: 10,
      defaultNumberOfPockets: 3,
      lockNumberOfStations: true,
      lockNumberOfPockets: true
    });
    setErrors({
      conveyorStartDelay: '',
      conveyorStopDelay: '',
      bufferHopperOpenDelay: '',
      bufferHopperCloseDelay: '',
      weighingDelay: '',
      zeroingDelay: '',
      zeroThresholdWeight: '',
      hopperCapacity: '',
      defaultInfeedTargetWeight: '',
      // Outfeed errors
      outfeedHopperCapacity: '',
      outfeedCycleWaitTime: '',
      outfeedWeighDelay: '',
      outfeedZeroDelay: '',
      outfeedZeroThresholdWeight: '',
      outfeedBreakoutAttempts: '',
      outfeedPocketBufferHopperCloseDelay: '',
    });
  };

  const handleDeleteAllEmployees = async () => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${apiUrl}/employees/delete-all`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      if (response.ok) {
        alert(`Successfully deleted ${result.deletedCount} employees.`);
        setShowDeleteEmployeesConfirm(false);
      } else {
        alert('Failed to delete employees: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting employees:', error);
      alert('Failed to delete employees. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-slate-200 rounded"></div>
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-2">System Default Settings</h3>
        <p className="text-sm text-slate-600">
          These values will be used as defaults when creating new lines. Existing lines will not be affected.
        </p>
      </div>

      {/* Sub-tabs for System/Infeed/Outfeed */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="-mb-px flex space-x-4" aria-label="System Defaults Sub-tabs">
          <button
            onClick={() => setActiveSubTab('system')}
            className={`py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'system' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            System
          </button>
          <button
            onClick={() => setActiveSubTab('infeed')}
            className={`py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'infeed' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Infeed Settings
          </button>
          <button
            onClick={() => setActiveSubTab('outfeed')}
            className={`py-2 px-3 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === 'outfeed' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            Outfeed Settings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeSubTab === 'system' && (
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-semibold text-slate-700 mb-4">Default Line Configuration</h4>
              <p className="text-sm text-slate-600 mb-6">
                Set the default values for new production lines. These can be customized for each line after creation.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Default Number of Stations
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.lockNumberOfStations}
                        onChange={(e) => setFormData(prev => ({ ...prev, lockNumberOfStations: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                      />
                      <span className="text-sm text-slate-600">Lock this value</span>
                    </label>
                  </div>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.defaultNumberOfStations}
                    onChange={(e) => handleSystemChange('defaultNumberOfStations', e.target.value === '' ? '' : Number(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.defaultNumberOfStations ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="10"
                  />
                  {errors.defaultNumberOfStations && (
                    <p className="mt-1 text-sm text-red-600">{errors.defaultNumberOfStations}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500">
                    The number of work stations per production line
                  </p>
                  {formData.lockNumberOfStations && (
                    <p className="mt-1 text-xs text-amber-600">
                      🔒 Users cannot change this when creating/editing lines
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Default Number of Pockets
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.lockNumberOfPockets}
                        onChange={(e) => setFormData(prev => ({ ...prev, lockNumberOfPockets: e.target.checked }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                      />
                      <span className="text-sm text-slate-600">Lock this value</span>
                    </label>
                  </div>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.defaultNumberOfPockets}
                    onChange={(e) => handleSystemChange('defaultNumberOfPockets', e.target.value === '' ? '' : Number(e.target.value))}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.defaultNumberOfPockets ? 'border-red-300' : 'border-slate-300'
                    }`}
                    placeholder="3"
                  />
                  {errors.defaultNumberOfPockets && (
                    <p className="mt-1 text-sm text-red-600">{errors.defaultNumberOfPockets}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500">
                    The number of product pockets for output collection
                  </p>
                  {formData.lockNumberOfPockets && (
                    <p className="mt-1 text-xs text-amber-600">
                      🔒 Users cannot change this when creating/editing lines
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Employee Management Section */}
            <div className="border-t pt-6 mt-6">
              <h4 className="text-md font-semibold text-slate-700 mb-4">Employee Management</h4>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">Danger Zone</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p className="mb-3">
                        <strong>Warning:</strong> This action will permanently delete ALL employees from the database. 
                        This cannot be undone. Use this only when you want to clear sample data and start fresh.
                      </p>
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setShowDeleteEmployeesConfirm(true)}
                        className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete All Employees
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeSubTab === 'infeed' && (
          <InfeedSettingsV2 
            infeedData={formData}
            onChange={handleInfeedChange}
            errors={errors}
            numberOfStations={Number(formData.defaultNumberOfStations) || 10}
          />
        )}
        
        {activeSubTab === 'outfeed' && (
          <OutfeedSettingsV2 
            outfeedData={{
              outfeedHopperCapacity: formData.outfeedHopperCapacity,
              outfeedCycleWaitTime: formData.outfeedCycleWaitTime,
              outfeedWeighDelay: formData.outfeedWeighDelay,
              outfeedZeroDelay: formData.outfeedZeroDelay,
              outfeedZeroThresholdWeight: formData.outfeedZeroThresholdWeight,
              outfeedBreakoutAttempts: formData.outfeedBreakoutAttempts,
              outfeedPocketBufferHopperCloseDelay: formData.outfeedPocketBufferHopperCloseDelay,
              pocketCloseDelays: formData.defaultPocketCloseDelays,
            }}
            numberOfPockets={4}
            onChange={handleOutfeedChange}
            onPocketDelayChange={handlePocketDelayChange}
            errors={errors}
            isSystemDefaults={true}
          />
        )}
      </div>
      
      {/* Save status message */}
      {saveMessage && (
        <div className={`mt-4 p-3 rounded-md ${
          saveMessage.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <p className="text-sm font-medium">{saveMessage.text}</p>
        </div>
      )}
      
      <div className="mt-8 flex justify-end space-x-3 pt-6 border-t border-slate-200">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
        >
          Reset to Factory Defaults
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Defaults'}
        </button>
      </div>
      
      {/* Delete Employees Confirmation Modal */}
      {showDeleteEmployeesConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">
                Delete All Employees?
              </h3>
            </div>
            
            <div className="mt-2 text-sm text-gray-600">
              <p className="mb-3">
                You are about to permanently delete <strong>ALL employees</strong> from the database.
              </p>
              <p className="mb-3 text-red-600 font-semibold">
                ⚠️ This action cannot be undone!
              </p>
              <p>
                This will remove all employee records, including:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Employee profiles</li>
                <li>Station assignments</li>
                <li>Time tracking data</li>
                <li>Performance metrics</li>
              </ul>
            </div>
            
            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowDeleteEmployeesConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAllEmployees}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete All Employees'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DefaultsSettings;